import * as React from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { type InputProps } from "../Input";

type InputTagsProps = Omit<InputProps, "value" | "onChange"> & {
  value: string[];
  onChange: React.Dispatch<React.SetStateAction<string[]>>;
};

const MAX_TAG_LENGTH = 50;

const InputTags = React.forwardRef<HTMLInputElement, InputTagsProps>(
  ({ className, value, onChange, ...props }, ref) => {
    const [pendingDataPoint, setPendingDataPoint] = React.useState("");
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

    const addPendingDataPoint = () => {
      const trimmedTag = pendingDataPoint.trim();
      if (trimmedTag.length > MAX_TAG_LENGTH) {
        setErrorMessage(`Tag must be ${MAX_TAG_LENGTH} characters or less.`);
        return;
      }
      
      if (trimmedTag && !value?.includes(trimmedTag)) {
        onChange([...value, trimmedTag]);
      }
      setPendingDataPoint("");
      setErrorMessage(null);
    };

    React.useEffect(() => {
      if (pendingDataPoint.includes(",")) {
        const newTags = pendingDataPoint
          .split(",")
          .map((tag) => tag.trim())
          .filter(
            (tag) =>
              tag.length > 0 &&
              tag.length <= MAX_TAG_LENGTH &&
              !value?.includes(tag)
          );

        if (newTags.length > 0) {
          onChange([...value, ...newTags]);
        }
        setPendingDataPoint("");
        setErrorMessage(null);
      }
    }, [pendingDataPoint, onChange, value]);

    return (
      <>
        <div
          className={cn(
            "has-[:focus-visible]:outline-none has-[:focus-visible]:ring-1 has-[:focus-visible]:ring-neutral-400 has-[:focus-visible]:ring-offset-2 dark:has-[:focus-visible]:ring-neutral-500 min-h-10 flex w-full flex-wrap gap-2 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm ring-offset-white disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700",
            className
          )}
        >
          {value?.map((item) => (
            <Badge key={item} variant="secondary">
              {item}
              <Button
                variant="ghost"
                size="icon"
                className="ml-2 h-3 w-3"
                onClick={() => onChange(value?.filter((i) => i !== item))}
              >
                <XIcon className="w-3" />
              </Button>
            </Badge>
          ))}
          <input
            className="flex-1 outline-none placeholder:text-neutral-500 dark:placeholder:text-neutral-400"
            value={pendingDataPoint}
            onChange={(e) => {
              const inputValue = e.target?.value;
              if (inputValue?.length > MAX_TAG_LENGTH) {
                setErrorMessage(
                  `Tag must be ${MAX_TAG_LENGTH} characters or less.`
                );
              } else {
                setErrorMessage(null);
              }
              setPendingDataPoint(inputValue);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addPendingDataPoint();
              } else if (
                e.key === "Backspace" &&
                pendingDataPoint.length === 0 &&
                value?.length > 0
              ) {
                e.preventDefault();
                onChange(value?.slice(0, -1));
              }
            }}
            placeholder={`Type a tag (max ${MAX_TAG_LENGTH} chars)...`}
            {...props}
            ref={ref}
          />
        </div>
        {errorMessage && (
          <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
        )}
      </>
    );
  }
);

InputTags.displayName = "InputTags";

export { InputTags };
